import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Shield, Lock, Eye, Trash2, Mail, Database } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: Database,
      title: "Données collectées",
      content: [
        "Lorsque vous utilisez notre site, nous pouvons collecter les informations suivantes :",
        "• **Formulaire de contact** : Nom, email, téléphone (optionnel), sujet et message",
        "• **Réservations** : Nom, email, nombre de participants pour les événements et workshops",
        "• **Données de navigation** : Informations techniques anonymes (type de navigateur, pages visitées)"
      ]
    },
    {
      icon: Eye,
      title: "Utilisation des données",
      content: [
        "Vos données personnelles sont utilisées uniquement pour :",
        "• Répondre à vos demandes de contact",
        "• Gérer vos réservations aux événements et workshops",
        "• Vous envoyer des confirmations de réservation par email",
        "• Améliorer notre site et nos services"
      ]
    },
    {
      icon: Lock,
      title: "Protection des données",
      content: [
        "Nous prenons la protection de vos données très au sérieux :",
        "• Vos données sont stockées de manière sécurisée sur des serveurs protégés",
        "• L'accès aux données est strictement limité au personnel autorisé",
        "• Nous utilisons le protocole HTTPS pour sécuriser les échanges",
        "• Les paiements sont traités via Stripe, un prestataire certifié PCI-DSS"
      ]
    },
    {
      icon: Shield,
      title: "Partage des données",
      content: [
        "Nous ne vendons ni ne partageons vos données personnelles avec des tiers, sauf :",
        "• **Stripe** : Pour le traitement sécurisé des paiements (soumis à leur propre politique de confidentialité)",
        "• **Obligations légales** : Si la loi nous y oblige"
      ]
    },
    {
      icon: Trash2,
      title: "Vos droits",
      content: [
        "Conformément au RGPD, vous disposez des droits suivants :",
        "• **Droit d'accès** : Obtenir une copie de vos données personnelles",
        "• **Droit de rectification** : Corriger des données inexactes",
        "• **Droit à l'effacement** : Demander la suppression de vos données",
        "• **Droit d'opposition** : Vous opposer au traitement de vos données",
        "Pour exercer ces droits, contactez-nous à : contact@tevaite-creation.com"
      ]
    },
    {
      icon: Mail,
      title: "Contact",
      content: [
        "Pour toute question concernant notre politique de confidentialité :",
        "• **Email** : contact@tevaite-creation.com",
        "• **Téléphone** : +689 89 29 29 30",
        "Nous nous engageons à répondre à vos demandes dans un délai de 30 jours."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#E8A5C4]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#C45B9F]/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div
            className={`text-center max-w-3xl mx-auto transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="text-[#C45B9F] font-medium uppercase tracking-wider text-sm">
              Informations légales
            </span>
            <h1 className="font-heading text-4xl lg:text-5xl text-[#3D2E35] mt-4">
              Politique de <span className="font-script text-[#8B4B7C]">Confidentialité</span>
            </h1>
            <p className="text-lg text-[#6B5A62] mt-6">
              La protection de vos données personnelles est notre priorité
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {sections.map((section, index) => (
              <div
                key={index}
                className={`card-tevaite p-6 lg:p-8 transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E8A5C4] to-[#C45B9F] flex items-center justify-center flex-shrink-0">
                    <section.icon className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-heading text-xl text-[#3D2E35] mb-4">
                      {section.title}
                    </h2>
                    <div className="space-y-2">
                      {section.content.map((paragraph, pIndex) => (
                        <p 
                          key={pIndex} 
                          className="text-[#6B5A62] leading-relaxed"
                          dangerouslySetInnerHTML={{
                            __html: paragraph
                              .replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#3D2E35]">$1</strong>')
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Last update */}
          <div className="mt-12 text-center text-[#6B5A62] text-sm">
            <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
